import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

/**
 * Flag to track if authentication has failed
 * Used to prevent continued sync operations
 */
export let isAuthFailed = false;

/**
 * Function to reset the auth failed state
 * Should be called after successful login/token refresh
 */
export const resetAuthFailedState = () => {
  console.log('Resetting auth failed state');
  isAuthFailed = false;
};

/**
 * Helper to check if an error is an authentication error
 */
export const isAuthenticationError = (error: any): boolean => {
  if (!error) return false;

  // Check for specific error codes
  if (error?.code === 16 && error?.message === 'authentication failed') return true;
  if (error?.code === 13 && error?.message === 'failed to authenticate user') return true;
  if (error?.code === 401 || error?.status === 401) return true;
  if (error?.response?.status === 401) return true;

  // Check for error messages
  const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  if (errorMessage.includes('authentication failed')) return true;
  if (errorMessage.includes('failed to authenticate')) return true;
  if (errorMessage.includes('unauthorized')) return true;
  if (errorMessage.includes('not authorized')) return true;
  if (errorMessage.includes('auth error')) return true;
  if (errorMessage.includes('token expired')) return true;

  return false;
};

/**
 * Hook to monitor for authentication errors and stop all sync operations
 * This should be used at the app root level
 */
export const useStopSyncOnAuthError = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Function to check for auth errors in the query cache
    const checkForAuthErrors = () => {
      if (isAuthFailed) return;

      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.getAll();

      // Check each query for auth errors
      for (const query of queries) {
        const state = query.state;
        if (state.status === 'error' && state.error) {
          if (isAuthenticationError(state.error)) {
            console.error('Auth error detected in query cache:', state.error);
            isAuthFailed = true;

            // Cancel all queries to prevent further API calls
            queryClient.cancelQueries();
            break;
          }
        }
      }
    };

    // Check immediately
    checkForAuthErrors();

    // Set up periodic checking
    const intervalId = setInterval(checkForAuthErrors, 1000);

    // Listen for error events from the query client
    const unsubscribe = queryClient.getQueryCache().subscribe((event: any) => {
      if (event?.type === 'error' && event?.error) {
        if (isAuthenticationError(event.error)) {
          console.error('Auth error detected in query event:', event.error);
          isAuthFailed = true;

          // Cancel all queries to prevent further API calls
          queryClient.cancelQueries();
        }
      }
    });

    return () => {
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [queryClient]);
};
